// 🛒 Orders dashboard (user 2026-10-15) — "like Shopify": orders, their value at OUR selling price (user 2026-10-23: not
// what the customer pays — no Jumia shipping, no voucher) and the profit they
// should bring. Default period = since the last shipment (the batch the warehouse ships next, like Daily Shipments).
// Backend: controllers/orders-dashboard.js. Profit is shown to admins only. Refreshes every minute.
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import ContextStore from '../hooks/ContextStore';
import { urls } from '../dispatch/urls';
import StoreScoreChip from '../components/common/StoreScoreChip';

interface Sum { orders: number; units: number; pays: number; profit?: number; expectedProfit?: number; avgOrder: number; cancelled: number; unknownCost: number }
interface Row { key: string; orders: number; units: number; pays: number; profit?: number }
interface Prod { name: string; image: string | null; units: number; pays: number; profit?: number; platforms: string[]; stock?: number | null; profitPerDay?: number | null }
interface Line { platform: string; number: string; at: string; store: string; product: string; image: string | null; units: number; pays: number; profit?: number | null; cancelled: boolean; strategy?: string | null; stock?: number | null; profitPerDay?: number | null }
interface Data {
  period: { key: string; label: string; start: string; end: string; cycle: boolean; starts?: Record<string, string> | null };
  rates?: Record<string, number>; summary: Sum; previous: Sum;
  byPlatform: Row[]; byStore: Row[]; products: Prod[]; timeline: { t: string; orders: number; pays: number }[]; latest: Line[];
  waitingFromBefore?: { orders: number; units: number; list: { platform: string; number: string; store: string; status: string; at: string; product: string; image: string | null; pays?: number; profit?: number | null; stock?: number | null; profitPerDay?: number | null }[] } | null;
}

const PERIODS = [['cycle', 'Since last shipment'], ['today', 'Today'], ['yesterday', 'Yesterday'], ['7d', '7 days'], ['30d', '30 days']] as const;
const PLAT: Record<string, { label: string; color: string }> = { jumia: { label: 'Jumia', color: '#f68b1e' }, marjan: { label: 'Marjane', color: '#e2001a' }, shopify: { label: 'Shopify', color: '#5e8e3e' } };
const mad = (n: number | undefined | null) => `${Math.round(n || 0).toLocaleString('fr-FR')} MAD`;
const hm = (d: string) => new Date(d).toISOString().slice(11, 16);   // Morocco = UTC
const dayHm = (d: string) => { const x = new Date(d); return `${x.toISOString().slice(8, 10)}/${x.toISOString().slice(5, 7)} ${hm(d)}`; };

// 📦 warehouse stock of the product (user 2026-10-25): red = none, orange = 5 or less
// the exact count is for admins only (user 2026-10-26) — the team sees in stock / low / out of stock
const StockTag: FC<{ n: number | null | undefined; admin?: boolean }> = ({ n, admin }) => {
  if (n == null) return null;
  const c = n <= 0 ? { bg: '#fee2e2', fg: '#b91c1c' } : n <= 5 ? { bg: '#fef3c7', fg: '#92400e' } : { bg: '#f1f5f9', fg: '#334155' };
  return <span title="Stock in the warehouse now" style={{ display: 'inline-block', marginTop: 2, padding: '0 6px', borderRadius: 999, background: c.bg, color: c.fg, fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' }}>📦 {n <= 0 ? 'out of stock' : admin ? `${n} in stock` : n <= 5 ? 'low stock' : 'in stock'}</span>;
};

// 💰 what the product earns us per day on average (normal pace × profit per piece at today's cost — admin only)
const PerDay: FC<{ v: number }> = ({ v }) => (
  <span title="Average profit per day of this product (normal monthly sales × profit per piece ÷ 30)" style={{ display: 'inline-block', marginTop: 2, padding: '0 6px', borderRadius: 999, background: v > 0 ? '#ecfdf5' : '#f1f5f9', color: v > 0 ? '#047857' : '#64748b', fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' }}>💰 {v > 0 ? `${v.toLocaleString('fr-FR')} MAD/day` : '< 1 MAD/day'}</span>
);

const Delta: FC<{ now: number; prev: number }> = ({ now, prev }) => {
  if (!prev) return null;
  const p = Math.round(((now - prev) / prev) * 100);
  return <span style={{ fontSize: 11.5, fontWeight: 700, color: p >= 0 ? '#047857' : '#b91c1c' }}>{p >= 0 ? '▲' : '▼'} {Math.abs(p)}%</span>;
};

const OrdersTodayPage: FC = () => {
  const { contextStore } = useContext(ContextStore);
  const admin = contextStore?.user?.type === 'admin';
  const api = useMemo(() => axios.create({ baseURL: `${urls.baseUrlRestApi}/profit`, headers: { 'x-auth-token': contextStore?.token || '' }, timeout: 120000 }), [contextStore?.token]);
  const [period, setPeriod] = useState<string>('cycle');
  const [data, setData] = useState<Data | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showWaiting, setShowWaiting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await api.get('/orders-dashboard', { params: { period } })).data); setErr(null); }
    catch (e: any) { setErr(e?.response?.data?.error?.message || e.message); }
    setLoading(false);
  }, [api, period]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const s = data?.summary, pv = data?.previous;
  const hourly = data ? new Date(data.period.end).getTime() - new Date(data.period.start).getTime() <= 2 * 864e5 : true;

  return (
    <div style={{ padding: 12, maxWidth: 1100, margin: '0 auto' }}>
      <h1 style={{ fontSize: 20, fontWeight: 800, margin: '4px 0' }}>🛒 Orders</h1>
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6, marginBottom: 6 }}>
        {PERIODS.map(([k, l]) => (
          <button key={k} onClick={() => setPeriod(k)} style={{ flex: '0 0 auto', minHeight: 36, padding: '0 14px', borderRadius: 18, border: '1px solid #e2e8f0', fontWeight: 700, fontSize: 13,
            background: period === k ? '#0f172a' : '#fff', color: period === k ? '#fff' : '#334155', cursor: 'pointer' }}>{l}</button>
        ))}
      </div>
      {data && (
        <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 10 }}>
          {data.period.cycle ? <>Orders received since the last parcels left — Jumia <b>{dayHm(data.period.starts?.jumia || data.period.start)}</b>, Marjane <b>{dayHm(data.period.starts?.marjan || data.period.start)}</b>. These are the orders we ship next.</>
            : <>{data.period.label}: {dayHm(data.period.start)} → {dayHm(data.period.end)}</>}
          {loading && <span> · updating…</span>}
        </div>
      )}
      {err && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 8 }}>⚠️ {err}</div>}
      {!data && !err && <div style={{ color: '#94a3b8' }}>Loading…</div>}

      {s && pv && (<>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 12 }}>
          <Kpi label="Orders" value={String(s.orders)} sub={<><Delta now={s.orders} prev={pv.orders} /> <span>{s.units} items{s.cancelled ? ` · ${s.cancelled} cancelled` : ''}</span></>} />
          <Kpi label="Sales (selling price)" value={mad(s.pays)} sub={<><Delta now={s.pays} prev={pv.pays} /> <span>avg {mad(s.avgOrder)} / order</span></>} />
          {admin && <Kpi label="Profit if all delivered" value={mad(s.profit)} color="#047857" sub={<><Delta now={s.profit || 0} prev={pv.profit || 0} /> {s.unknownCost ? <span style={{ color: '#b45309' }}>{s.unknownCost} without cost</span> : null}</>} />}
          {admin && <Kpi label="Expected profit" value={mad(s.expectedProfit)} color="#0f766e"
            sub={<span>after returns & cancels{data.rates ? ` (Jumia ${Math.round(data.rates.jumia * 100)}%, Marjane ${Math.round(data.rates.marjan * 100)}% delivered)` : ''}</span>} />}
        </div>
        {data.waitingFromBefore && data.waitingFromBefore.orders > 0 && (
          <div style={{ margin: '-4px 0 12px' }}>
            <button onClick={() => setShowWaiting((v) => !v)} style={{ border: '1px solid #fcd34d', background: '#fffbeb', color: '#92400e', borderRadius: 10, padding: '6px 10px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
              ⏳ +{data.waitingFromBefore.orders} from before still to ship {showWaiting ? '▴' : '▾'}
            </button>
            {showWaiting && <div style={{ marginTop: 6, border: '1px solid #fde68a', borderRadius: 10, background: '#fff', padding: '4px 10px' }}>
              {data.waitingFromBefore.list.map((w, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: i ? '1px solid #f1f5f9' : 'none' }}>
                  {w.image ? <img src={w.image} alt="" style={{ width: 34, height: 34, objectFit: 'contain', borderRadius: 6, background: '#fff' }} /> : null}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.product}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{dayHm(w.at)} · {PLAT[w.platform]?.label} · {w.store} · #{w.number}</div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 12.5 }}>
                    {w.pays ? <div style={{ fontWeight: 700 }}>{mad(w.pays)}</div> : null}
                    <StockTag n={w.stock} admin={admin} />
                    {admin && w.profit != null && <div style={{ color: w.profit >= 0 ? '#047857' : '#b91c1c', fontSize: 11.5 }}>{w.profit >= 0 ? '+' : ''}{mad(w.profit)}</div>}
                    {admin && w.profitPerDay != null && <PerDay v={w.profitPerDay} />}
                  </div>
                </div>
              ))}
            </div>}
          </div>
        )}
        <div style={{ fontSize: 11.5, color: '#94a3b8', margin: '-6px 0 12px' }}>▲▼ compared with {data.period.cycle || data.period.key === 'today' ? 'the same time yesterday' : 'the period just before'}.</div>

        <Card title={hourly ? 'Orders per hour' : 'Orders per day'}>
          <div style={{ width: '100%', height: 180 }}>
            <ResponsiveContainer>
              <BarChart data={data.timeline.map((t) => ({ ...t, label: hourly ? `${t.t.slice(11, 13)}h` : `${t.t.slice(8, 10)}/${t.t.slice(5, 7)}` }))}>
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} width={28} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v: any, n: any) => (n === 'pays' ? [mad(v), 'sales (selling price)'] : v)} labelStyle={{ fontWeight: 700 }} />
                <Bar dataKey="orders" fill="#f68b1e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
          <Card title="By platform">
            {data.byPlatform.length ? data.byPlatform.map((r) => <Line3 key={r.key} dot={PLAT[r.key]?.color} name={PLAT[r.key]?.label || r.key} r={r} admin={admin} />) : <Empty />}
          </Card>
          <Card title="By store">
            {data.byStore.length ? data.byStore.map((r) => <Line3 key={r.key} name={r.key} r={r} admin={admin} score />) : <Empty />}
          </Card>
        </div>

        <Card title="Top products">
          {data.products.length ? data.products.map((p, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: i ? '1px solid #f1f5f9' : 'none' }}>
              {p.image ? <img src={p.image} alt="" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 8, background: '#f8fafc', flex: '0 0 auto' }} /> : <div style={{ width: 40, height: 40, borderRadius: 8, background: '#f1f5f9' }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                <div style={{ fontSize: 11.5, color: '#64748b' }}>{p.platforms.map((x) => PLAT[x]?.label || x).join(' · ')}</div>
              </div>
              <div style={{ textAlign: 'right', fontSize: 12.5 }}>
                <div><b>×{p.units}</b> · {mad(p.pays)}</div>
                <StockTag n={p.stock} admin={admin} />
                {admin && <div style={{ color: '#047857', fontSize: 11.5 }}>+{mad(p.profit)}</div>}
                {admin && p.profitPerDay != null && <PerDay v={p.profitPerDay} />}
              </div>
            </div>
          )) : <Empty />}
        </Card>

        <Card title="Latest orders">
          {data.latest.length ? data.latest.map((l, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: i ? '1px solid #f1f5f9' : 'none', opacity: l.cancelled ? 0.45 : 1 }}>
              <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 2, background: PLAT[l.platform]?.color || '#cbd5e1' }} />
              {l.image ? <img src={l.image} alt="" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 6, background: '#f8fafc', flex: '0 0 auto' }} /> : null}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: l.cancelled ? 'line-through' : 'none' }}>{l.product}{l.units > 1 ? ` ×${l.units}` : ''}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{dayHm(l.at)} · {PLAT[l.platform]?.label} · {l.store}{l.platform === 'jumia' && <StoreScoreChip store={l.store} />}{l.number ? ` · #${l.number}` : ''}{l.cancelled ? ' · cancelled' : ''}
                  {l.strategy && <span title="This copy was created by a listing strategy" style={{ marginLeft: 6, padding: '0 6px', borderRadius: 999, background: '#ecfdf5', color: '#047857', fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap' }}>🌱 {l.strategy}</span>}</div>
              </div>
              <div style={{ textAlign: 'right', fontSize: 12.5 }}>
                <div style={{ fontWeight: 700 }}>{mad(l.pays)}</div>
                <StockTag n={l.stock} admin={admin} />
                {admin && l.profit != null && <div style={{ color: l.profit >= 0 ? '#047857' : '#b91c1c', fontSize: 11.5 }}>{l.profit >= 0 ? '+' : ''}{mad(l.profit)}</div>}
                {admin && l.profitPerDay != null && <PerDay v={l.profitPerDay} />}
              </div>
            </div>
          )) : <Empty />}
        </Card>
      </>)}
    </div>
  );
};

const Kpi: FC<{ label: string; value: string; sub?: React.ReactNode; color?: string }> = ({ label, value, sub, color }) => (
  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '10px 12px' }}>
    <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color: color || '#0f172a', margin: '2px 0' }}>{value}</div>
    <div style={{ fontSize: 11.5, color: '#64748b', display: 'flex', gap: 6, flexWrap: 'wrap' }}>{sub}</div>
  </div>
);
const Card: FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 12, marginBottom: 12 }}>
    <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 6 }}>{title}</div>{children}
  </div>
);
const Line3: FC<{ name: string; r: Row; admin: boolean; dot?: string; score?: boolean }> = ({ name, r, admin, dot, score }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: '1px solid #f1f5f9', fontSize: 13 }}>
    {dot && <span style={{ width: 10, height: 10, borderRadius: 5, background: dot }} />}
    <span style={{ flex: 1, fontWeight: 600 }}>{name}{score && <StoreScoreChip store={name} />}</span>
    <span style={{ color: '#64748b' }}>{r.orders} order{r.orders === 1 ? '' : 's'}</span>
    <span style={{ fontWeight: 700, minWidth: 80, textAlign: 'right' }}>{mad(r.pays)}</span>
    {admin && <span style={{ color: '#047857', minWidth: 70, textAlign: 'right' }}>+{mad(r.profit)}</span>}
  </div>
);
const Empty: FC = () => <div style={{ fontSize: 13, color: '#94a3b8' }}>No order yet.</div>;

export default OrdersTodayPage;
